import React, { useEffect, useState } from 'react';
import { Icon } from './Icon.js';
import './ImageViewer.css';

const ImageViewer = ({ images = [], initialIndex = 0, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset current index when modal opens with new images
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, onClose]);

  const goToPrevious = () => {
    setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };

  const goToNext = () => {
    setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  const handleOverlayClick = (e) => {
    console.log('Overlay clicked!', e.target.className);
    onClose();
  };

  const handleModalClick = (e) => {
    console.log('Modal clicked!', e.target.className);
    e.stopPropagation();
  };

  if (!isOpen || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];

  return (
    <div className="image-viewer-overlay" onClick={handleOverlayClick}>
      <div className="image-viewer-modal" onClick={handleModalClick}>
        {/* Main image */}
        <div className="image-viewer-main">
          <img
            src={currentImage.image_url}
            alt={`Image ${currentIndex + 1} of ${images.length}`}
            className="image-viewer-main-image"
          />
          
          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button 
                className="image-viewer-nav image-viewer-nav--prev" 
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <Icon name="ChevronLeft" className="icon--lg" />
              </button>
              <button 
                className="image-viewer-nav image-viewer-nav--next" 
                onClick={goToNext}
                aria-label="Next image"
              >
                <Icon name="ChevronRight" className="icon--lg" />
              </button>
            </>
          )}
        </div>

        {/* Image counter */}
        {images.length > 1 && (
          <div className="image-viewer-counter">
            {currentIndex + 1} of {images.length}
          </div>
        )}

        {/* Thumbnail navigation */}
        {images.length > 1 && (
          <div className="image-viewer-thumbnails">
            {images.map((image, index) => (
              <button
                key={image.id}
                className={`image-viewer-thumbnail ${index === currentIndex ? 'image-viewer-thumbnail--active' : ''}`}
                onClick={() => goToImage(index)}
                aria-label={`Go to image ${index + 1}`}
              >
                <img
                  src={image.image_url}
                  alt={`Thumbnail ${index + 1}`}
                  className="image-viewer-thumbnail-image"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageViewer;
